import { cva, VariantProps } from 'class-variance-authority';
import { createMeshGradient, createLinearGradient } from '@/lib/meshGradient';

/**
 * Helper function to get random samples from an array
 */
export function getRandomSamples<T>(array: T[], count: number): T[] {
  if (array.length <= count) return array;
  const shuffled = [...array].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, count);
}

// Define the variants using class-variance-authority
export const photoPackVariants = cva('relative transition-colors duration-200 border-0 group overflow-hidden', {
  variants: {
    variant: {
      listItem: 'w-full',
      samples: 'w-full  md:hover:opacity-80',
      'samples-w-cta': 'w-full hover:cursor-default',
      'mesh-gradient': 'w-full hover:cursor-pointer md:hover:opacity-80',
      hero: 'w-full hover:cursor-pointer md:hover:opacity-80'
    },
    checked: {
      true: 'ring-4 dark:ring-muted ring-black/10 ring-offset-2 ring-offset-accent'
    }
  },
  defaultVariants: {
    variant: 'samples',
    checked: false
  }
});

export type PhotoPackVariant = VariantProps<typeof photoPackVariants>['variant'];

/**
 * Helper function to get background style based on variant and preferences
 */
export const getBackgroundStyle = ({
  singleColor,
  useMeshGradient,
  variant,
  primaryDark,
  secondaryDark,
  primaryDarkColor,
  secondaryDarkColor,
  style
}: {
  singleColor?: string;
  useMeshGradient?: boolean;
  variant?: string;
  primaryDark?: string;
  secondaryDark?: string;
  primaryDarkColor?: string | null;
  secondaryDarkColor?: string | null;
  style?: React.CSSProperties;
}) => {
  // Get colors - prefer stored colors if available
  const primary = primaryDarkColor || primaryDark;
  const secondary = secondaryDarkColor || secondaryDark;

  // Create gradients for styling
  const gradients = {
    linear: createLinearGradient(primary, secondary),
    mesh: createMeshGradient(primary, secondary)
  };

  // If a single color is provided, use it for the gradient
  if (singleColor) {
    return {
      backgroundColor: singleColor,
      backgroundImage: `linear-gradient(to bottom, ${singleColor}00, ${singleColor})`,
      ...style
    };
  }

  // If mesh gradient is enabled, apply it to all variants
  if (useMeshGradient || variant === 'mesh-gradient') {
    return {
      ...gradients.mesh,
      ...style
    };
  }

  // For cover variant without mesh, just use the provided style
  if (variant === 'cover') {
    return style;
  }

  // For other variants without mesh, use linear gradient
  return {
    background: gradients.linear,
    ...style
  };
};

/**
 * Helper hook to use background styles based on image URL and variant
 */
export const useBackgroundStyle = ({
  useMeshGradient,
  variant,
  singleColor,
  primaryDarkColor,
  secondaryDarkColor,
  style
}: {
  useMeshGradient?: boolean;
  variant?: string;
  singleColor?: string;
  primaryDarkColor?: string | null;
  secondaryDarkColor?: string | null;
  style?: React.CSSProperties;
}) => {
  return getBackgroundStyle({
    singleColor,
    useMeshGradient,
    variant,
    primaryDark: primaryDarkColor || '',
    secondaryDark: secondaryDarkColor || '',
    primaryDarkColor,
    secondaryDarkColor,
    style
  });
};
